import { normalizeMemberCountryCode } from '../location/memberLocation';

type KindredContentCountry = 'SHARED' | 'GB' | 'IE' | 'AU' | 'NZ' | 'CA' | 'US';

const resolveKindredContentCountry = (countryCode?: string | null): KindredContentCountry => {
  const normalized = countryCode ? normalizeMemberCountryCode(countryCode) : null;
  if (
    normalized === 'GB' ||
    normalized === 'IE' ||
    normalized === 'AU' ||
    normalized === 'NZ' ||
    normalized === 'CA' ||
    normalized === 'US'
  ) {
    return normalized;
  }

  return 'SHARED';
};

const KINDRED_COUNTRY_CONTENT = {
  SHARED: {
    publicTagline: 'Free community for single parent families',
    supportFeatureDescription:
      'Community, activities, and practical support in one place — with local guidance rolling out country by country.',
    editorialScopeBullet:
      'Editorial support guidance is currently live for the UK, Australia, New Zealand, and the United States. Ireland is paused while the EEA representative position is resolved.',
    editorialSourceBullets: [
      'Official government, health, or public-service guidance for the country named on the page where relevant',
      'Recognised public, charity-sector, or support organisations where they add practical value',
      'Country-appropriate onward links so you can verify the current position yourself',
      'Honest "coming soon" coverage where fuller local support guidance is not ready yet',
    ],
  },
  GB: {
    publicTagline: 'Free community for single parent families in the UK',
    supportFeatureDescription: 'Benefits, housing, legal rights — clear local guidance for the UK',
    editorialScopeBullet: 'Editorial content written for single-parent family life in the UK',
    editorialSourceBullets: [
      'Official UK government guidance where relevant',
      'NHS guidance for health-related signposting',
      'Recognised public, charity-sector, or support organisations where they add practical value',
      'Clear onward links so you can verify the current position yourself',
    ],
  },
  IE: {
    publicTagline: 'Ireland rollout paused',
    supportFeatureDescription:
      'Ireland is paused while Kindred resolves the EEA representative position before reopening local guidance.',
    editorialScopeBullet:
      'Ireland support content is prepared but paused until the EEA representative position is resolved.',
    editorialSourceBullets: [
      'Ireland is not currently treated as an active Kindred launch market',
      'The pause is because the GDPR Article 27 / EEA representative position needs resolving first',
      'Ireland should not be re-enabled in public rollout wording until that layer is ready',
    ],
  },
  AU: {
    publicTagline: 'Free community for single parent families in Australia',
    supportFeatureDescription:
      'Benefits, housing, legal rights, and practical next steps — clear local guidance for Australia.',
    editorialScopeBullet:
      'Editorial content written for single-parent family life in Australia, using national sources first and state or territory routes where the system changes locally.',
    editorialSourceBullets: [
      'Official Australian government or public-service guidance where relevant',
      'Recognised public, charity-sector, or support organisations where they add practical value',
      'Clear onward links so you can verify the current position yourself',
    ],
  },
  NZ: {
    publicTagline: 'Free community for single parent families in New Zealand',
    supportFeatureDescription:
      'Benefits, housing, legal rights, and practical next steps — clear local guidance for New Zealand.',
    editorialScopeBullet:
      'Editorial content written for single-parent family life in New Zealand, using national official sources first and clearer signposting where access still varies locally.',
    editorialSourceBullets: [
      'Official New Zealand government or public-service guidance where relevant',
      'Recognised public, charity-sector, or support organisations where they add practical value',
      'Clear onward links so you can verify the current position yourself',
    ],
  },
  CA: {
    publicTagline: 'Canada rollout paused',
    supportFeatureDescription:
      'Canada is paused while Kindred prepares a proper Quebec/French public, legal, and support layer.',
    editorialScopeBullet:
      'Canada is paused for later until a proper Quebec/French public, legal, and support layer is ready.',
    editorialSourceBullets: [
      'Canada is not currently treated as an active or near-term Kindred launch market',
      'The pause is because Quebec/French public, legal, and support coverage still needs a proper layer',
      'Canada should not be re-enabled in public rollout wording until that layer is ready',
    ],
  },
  US: {
    publicTagline: 'Free community for single parent families in the USA',
    supportFeatureDescription:
      'Benefits, housing, legal rights, school support, and work guidance - clear U.S. signposting with state and local routing where the rules change.',
    editorialScopeBullet:
      'Editorial content written for single-parent family life in the United States, using national explainers first and then routing to state, district, tribal, or local official sources where the next step changes by place.',
    editorialSourceBullets: [
      'Official US government or public-service guidance where relevant',
      'Recognised public, charity-sector, or support organisations where they add practical value',
      'National directory and handoff pages where the correct next source depends on state, district, tribe, or local area',
      'Clear onward links so you can verify the current position yourself',
    ],
  },
} as const;

export const KINDRED_PHASE_ONE_ROLLOUT_COPY =
  'Local support guidance is currently live for the UK, Australia, New Zealand, and the United States, with the U.S. set using one shared national library plus state, district, tribal, and local routing where the system changes by place. Ireland is paused while Kindred resolves the EEA representative position.';

export const getKindredPublicTagline = (countryCode?: string | null) =>
  KINDRED_COUNTRY_CONTENT[resolveKindredContentCountry(countryCode)].publicTagline;

export const getKindredSupportFeatureDescription = (countryCode?: string | null) =>
  KINDRED_COUNTRY_CONTENT[resolveKindredContentCountry(countryCode)].supportFeatureDescription;

export const getKindredEditorialScopeBullet = (countryCode?: string | null) =>
  KINDRED_COUNTRY_CONTENT[resolveKindredContentCountry(countryCode)].editorialScopeBullet;

export const getKindredEditorialSourceBullets = (countryCode?: string | null) =>
  [...KINDRED_COUNTRY_CONTENT[resolveKindredContentCountry(countryCode)].editorialSourceBullets];
